import React, { useContext, useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import { observer } from 'mobx-react-lite';
import Dialog from 'libraries/Dialog';
import TextInputOutlined from 'libraries/TextInput';
import { generateSetDescription, updateSet } from 'requests/CMSRequests';
import CircularProgress from '@mui/material/CircularProgress';
import { appCtx } from 'components/appStore';
import IconButton from '@mui/material/IconButton';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import Box from '@mui/material/Box';

const EditSetDialog = observer(({ onSuccess, set, openedDialog, handleDialog }) => {
    const [loading, setLoading] = useState(false);
    const [skillName, setSkillName] = useState('');
    const [description, setDescription] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    // New state for AI generation loading
    const [generatingDescription, setGeneratingDescription] = useState(false);

    const { flashMessage } = useContext(appCtx);

    useEffect(() => {
        setSkillName(set ? set.title : '');
        setDescription(set ? set.description : '');
        setErrorMessage('');
    }, [set]);


    const handleSkillName = (e) => {
        setSkillName(e.target.value);
        setErrorMessage('');
    };

    const handleDescription = (e) => {
        setDescription(e.target.value);
        setErrorMessage('');
    };

    const submit = () => {
        setLoading(true);
        updateSet(set.uuid, skillName, description)
            .then(({ success, data }) => {
                if (success && data) {
                    handleDialog();
                    flashMessage('Set updated', 'done');
                    if (onSuccess) onSuccess(data);
                }
            })
            .catch((err) => {
                if (err.response && err.response.data.msg) {
                    setErrorMessage(err.response.data.msg);
                }
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const generateDescription = () => {
        setGeneratingDescription(true);
        generateSetDescription(set.uuid)
            .then(({ success, data }) => {
                if (success && data) {
                    flashMessage('Description is generated!', 'done');
                    if (onSuccess) setDescription(data);
                }
            })
            .catch((err) => {
                if (err.response && err.response.data.msg) {
                    setErrorMessage(err.response.data.msg);
                }
            })
            .finally(() => {
                setGeneratingDescription(false);
            });
    };

    return (
        <Dialog
            onClose={handleDialog}
            handleClose={handleDialog}
            titleComponent="Edit Set"
            maxWidth="sm"
            actionComponent={(
                <>
                    <Button
                        className="u-txt--bold"
                        onClick={handleDialog}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        className="u-pdn--rx3 u-pdn--lx3"
                        disabled={!skillName || !skillName.trim() || loading}
                        onClick={submit}
                    >
                        {
                            loading ? (
                                <CircularProgress
                                    size={24}
                                    thickness={3}
                                />
                            ) : <span>Save</span>
                        }
                    </Button>
                </>
            )}
            open={openedDialog}
        >

            <TextInputOutlined
                onChange={handleSkillName}
                value={skillName}
                variant="outlined"
                maxLength={255}
                minLength={1}
                hasCounter
                placeholder="Enter your new set name"
                label="Set Name"
                isError={Boolean(errorMessage)}
                helperText={errorMessage}
            />
            <TextInputOutlined
                onChange={handleDescription}
                value={description}
                variant="outlined"
                maxLength={10000}
                minLength={1}
                hasCounter
                multiline
                rows={6}
                placeholder="Enter the description for the set"
                label="Set Description"
                isError={Boolean(errorMessage)}
                helperText={errorMessage}
                endAdornment={(
                    <Box
                        sx={{
                            position: 'absolute',
                            top: -27,
                            right: 2,
                            zIndex: 1
                        }}
                    >
                        <IconButton
                            onClick={generateDescription}
                            disabled={generatingDescription}
                            title="AI Generate Description"
                            size="small"
                            edge="end"
                        >
                            {generatingDescription ? (
                                <CircularProgress size={20} />
                            ) : (
                                <SmartToyIcon color='primary' />
                            )}
                        </IconButton>
                    </Box>
                )}
            />
        </Dialog>
    );
});

export default EditSetDialog;
